/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import { ProductName } from '@pypestream/api-services';
import { html, nothing, unsafeCSS } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { property, state } from 'lit/decorators.js';
import { TreeUtils } from 'simple-tree-utils';

import { i18n } from '@pypestream/translations';
import { BaseElement, customElement } from '../../components';
import { watch } from '../../components/base-element';
import { TreeItemWC } from '../../components/tree-item/tree-item.wc';
import { handleGoHome, sortByKeyRecursively } from '../utils';
import { smartService } from '../xstate/smart.xstate';
import { SmartContext } from '../xstate/smart.xstate-utils';
import { CommonProject, OrgSelectorState, OrgWithChildren } from './types';

import '../../components/buttons/icon-button/icon-button.wc';
import '../../components/icon/icon.wc';
import '../../components/menu/src/menu.wc';
import '../../components/tree/tree.wc';
import './universal-nav-tree-child.wc';

import styles from './universal-nav-org-selector.scss?inline';
import { setOrgsVisibility } from '../utils/set-orgs-visibility';
import { logos } from '../../assets/logos';

const treeUtils = new TreeUtils({
  idProp: 'id', // the key of a unique identifier for an object (source object)
  parentIdProp: 'parentId', // the key of a unique parent identifier (source object)
  childrenProp: 'children', // the key, where child nodes are stored (destination object tree)
});

@customElement('ps-menu-org-selector')
export class OrgSelectorWC extends BaseElement {
  static styles = unsafeCSS(styles);

  context: SmartContext;

  @property({ type: String }) orgId: string | undefined;

  @property() onSelectionChange: (orgId: string | null) => void;

  @property({ type: Boolean }) isDisabled: boolean = false;

  @property({ reflect: false, type: Boolean }) hideGoHome?: boolean = false;

  @state() private localState: OrgSelectorState = {
    orgs: [],
    orgTree: [],
  };

  @state() private _hasAvailableTools?: boolean = undefined;

  @state() private _accountId?: string = undefined;

  @state() private _assignedProjects?: CommonProject[] = undefined;

  @watch(['_hasAvailableTools', '_accountId', '_assignedProjects'])
  handleHasAvailableToolsChange() {
    if (
      this.context.app !== ProductName.Organization &&
      this._hasAvailableTools &&
      this._accountId &&
      this._assignedProjects
    ) {
      this.onSelectionChange(this._accountId);
    }
  }

  constructor() {
    super();
    this.init = this.init.bind(this);
  }

  connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();

    smartService.subscribe((smartState) => {
      const currentUserInfo = this.context?.userInfo;
      const currentHasAvailableTools = this.context?.hasAvailableTools;
      const currentAccountId = this.context?.accountId;
      const assignedProjects = this.context?.assignedProjects;
      const allAssignedProjects = this.context?.allAssignedProjects;

      this.context = smartState.context;

      if (
        currentUserInfo !== smartState.context.userInfo ||
        currentHasAvailableTools !== smartState.context.hasAvailableTools ||
        currentAccountId !== smartState.context.accountId ||
        assignedProjects !== smartState.context.assignedProjects ||
        allAssignedProjects !== smartState.context.allAssignedProjects
      ) {
        this.init();
      }
    });
  }

  init() {
    const { userInfo, hasAvailableTools, accountId, assignedProjects } =
      this.context;

    this._hasAvailableTools = hasAvailableTools;

    this._assignedProjects = assignedProjects;

    this._accountId = accountId;

    const defaultAccount = userInfo?.defaultAccount;

    if (defaultAccount) {
      const { allChildAccounts, ...orgProps } = defaultAccount;

      this.localState.orgs = [orgProps].concat(allChildAccounts || []);
    }

    const orgsWithChildren: OrgWithChildren[] = treeUtils.list2Tree(
      (this.localState.orgs || []).map((org) => {
        if (org.id === this.context?.userInfo?.defaultAccount?.id) {
          return { ...org, parentId: null };
        }
        return org;
      }) || []
    ) as OrgWithChildren[];

    orgsWithChildren.map((org) => {
      if ('id' in org) {
        org.selected = org?.id === this.orgId;
      }

      if ('children' in org === undefined) {
        org.children = [];
      }

      return org;
    }) as OrgWithChildren[];

    const maxDepth = (root: OrgWithChildren) => {
      // if root is undefined, depth is 0
      if (!root) return 0;
      // variable to store the maximum levels
      let max = 0;
      // helper function to traverse the tree
      // recursively increment the levels by one
      const dfs = (node: OrgWithChildren, levels: number) => {
        // console.log(node.name, levels);

        node.depth = levels;
        // compare levels and max to pass the maximum levels
        if (levels > max) max = levels;
        // traverse all children of the current node
        for (const child of node.children) {
          // increment the levels by one
          dfs(child, levels + 1);
        }
      };
      // when root is not null, the tree has at least one level,
      // so we pass down 1
      dfs(root, 1);

      // return the maximum levels
      return max;
    };

    maxDepth(orgsWithChildren[0]);

    if (
      orgsWithChildren[0] &&
      this.context.allAssignedProjects.length &&
      this.context.userInfo?.settings?.smartOrgMenuEnabled
    ) {
      setOrgsVisibility(orgsWithChildren[0], this.context.allAssignedProjects);
    }

    this.localState = {
      ...this.localState,
      orgTree: orgsWithChildren,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  renderGoToHome() {
    if (this.hideGoHome) return nothing;

    return html`
      <div class="c-u-nav-org-selector__home-button">
        <ps-button
          variant="ghost"
          width="full"
          size="large"
          @click=${() =>
            handleGoHome({
              accountId: this.context?.accountId,
              app: this.context?.app,
            })}
          id="go-home-button"
        >
          ${i18n.t('navigation:mega.sidebar.goHome') || 'Go Home'}
          <ps-image
            src=${logos.home}
            width="24px"
            height="24px"
            slot="prefix"
            radius="small"
          ></ps-image>
        </ps-button>
      </div>
    `;
  }

  render() {
    const { orgTree, orgs } = this.localState;

    const topOrgname = 'Pypestream';
    const sortingKey = 'name';

    const pypeOrg = orgs?.filter((org) => org.name === topOrgname) || [];
    const restOrgs = sortByKeyRecursively(
      orgs?.filter((org) => org.name !== topOrgname) || [],
      sortingKey
    );
    const sortedOrgs = pypeOrg.concat(restOrgs);

    const treeState = orgTree?.length
      ? sortByKeyRecursively(orgTree, sortingKey)
      : sortedOrgs;

    return html`
      <div class="c-u-nav-org-selector">
        ${this.renderGoToHome()}
        <ps-tree
          class="c-u-nav-org-selector__tree"
          @pl-selection-change=${({
            detail: { selection },
          }: {
            detail: { selection: TreeItemWC[] };
          }) => {
            const newOrgId = selection[0]?.getAttribute('data-org-id');

            if (!newOrgId) {
              throw new Error('No org id found');
            }

            if (this.context?.app === ProductName.Organization) {
              this.onSelectionChange(newOrgId);
            } else {
              smartService.send({
                type: 'changeOrgSelection',
                orgId: newOrgId,
              });
            }
          }}
        >
          ${treeState?.map(
            (item) => html`
              <ps-universal-nav-tree-child
                .child=${item}
                orgId=${ifDefined(
                  this.context.changeOrgSelection || this.orgId
                )}
                ?isDisabled=${this.isDisabled}
                ?isSmartMenuEnabled=${this.context.userInfo?.settings
                  ?.smartOrgMenuEnabled}
              ></ps-universal-nav-tree-child>
            `
          )}
        </ps-tree>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-menu-org-selector': OrgSelectorWC;
  }
  enum PSElementTagNameMap {
    'ps-menu-org-selector' = 'ps-menu-org-selector',
  }
}
